import { defaultFilters } from "../providers/FiltersProvider";
import type { Filters } from "./navFilterUtils";
import { cleanString, getModels, getYears } from "./navFilterUtils";
import type { RootNode } from "@dealer-center/ddc-pages-incentives-manager-lambda";

export function getMakeFromText(
  text: string,
  accountMakes: string[],
  filters: Filters
): string {
  const makeText = cleanString(text);
  const makes = accountMakes.map(make => cleanString(make));

  let withinStrIdx = -1;
  let wordLength = 0;
  const makeIdx = makes.findIndex(make => {
    withinStrIdx = makeText.indexOf(make);
    if (withinStrIdx !== -1) {
      wordLength = make.length;
      if (
        withinStrIdx + wordLength === makeText.length ||
        makeText[withinStrIdx + wordLength] === " "
      ) {
        return withinStrIdx !== -1;
      }
    }
    return make.split(/[- ]+/).find(word => {
      withinStrIdx = makeText.indexOf(word);
      if (withinStrIdx !== -1) {
        wordLength = word.length;
        if (
          withinStrIdx + wordLength === makeText.length ||
          makeText[withinStrIdx + wordLength] === " " ||
          makeText[withinStrIdx + wordLength] === "-"
        ) {
          return true;
        }
      }
      return false;
    });
  });
  if (makeIdx !== -1) {
    filters.make = accountMakes[makeIdx];
    filters.year = null;
    filters.model = null;
    filters.trim = null;
  }
  if (withinStrIdx !== -1) {
    // remove the make from the text
    const txt =
      text.substring(0, withinStrIdx) +
      text.substring(withinStrIdx + wordLength);
    return txt;
  }
  return text;
}

export function getYearsFromText(
  txt: string,
  filters: Filters,
  navtree: RootNode | null = null
): string {
  let text = txt;
  let scratchText = "";
  let singleYear: number | null = null;
  const results: number[] = [];
  let yearMatch = text.match(/(\d{3}0s)/g);
  if (yearMatch) {
    if (yearMatch) {
      yearMatch.forEach(yearStr => {
        text = text.replace(yearStr, "");
        scratchText += yearStr + " ";
      });

      const actualYears: number[] = yearMatch
        .filter(yearStr => {
          const year = parseInt(yearStr.replace("s", ""), 10);
          return year >= 1920 && year <= new Date().getFullYear() + 1;
        })
        .map(yearStr => parseInt(yearStr, 10));
      const range: number[] = [];
      actualYears.forEach((year, idx) => {
        for (let y = actualYears[idx]; y <= actualYears[idx] + 9; y++) {
          range.push(y);
        }
      });
      results.push(...range);
    }
  }
  yearMatch = text.match(/(\d{4})/g);
  if (yearMatch) {
    const actualYears: number[] = yearMatch
      .filter(yearStr => {
        const year = parseInt(yearStr, 10);
        return year >= 1920 && year <= new Date().getFullYear() + 1;
      })
      .map(yearStr => parseInt(yearStr, 10));

    if (actualYears.length > 0) {
      let isNextDone = false;
      actualYears.forEach((year, yearIdx) => {
        if (!isNextDone) {
          if (yearIdx < actualYears.length - 1) {
            let startidx = text.indexOf(year.toString());
            if (startidx !== -1) {
              startidx += 4;
            }
            const nextidx = text.indexOf(actualYears[yearIdx + 1].toString());
            const between = text.substring(startidx, nextidx).trim();
            if (
              between.includes("-") ||
              between.includes("to") ||
              between.includes("à")
            ) {
              isNextDone = true;
              const range: number[] = [];
              for (let y = year; y <= actualYears[yearIdx + 1]; y++) {
                range.push(y);
              }
              if (range.length > 0) {
                results.push(...range);
              }
              scratchText +=
                " " + year + between + actualYears[yearIdx + 1] + " ";
              text = text.replace(/-|to|à/g, " ");
              actualYears.forEach(year => {
                text = text.replace(year.toString(), "");
              });
            } else {
              results.push(year);
              text = text.replace(year.toString(), "");
              scratchText += year.toString() + " ";
            }
          } else {
            singleYear = year;
            results.push(year);
            text = text.replace(year.toString(), "");
          }
        } else {
          isNextDone = false;
        }
      });
    }
  }
  if (results.length > 0) {
    // remove duplicates
    const map = new Map<number, boolean>();
    results.forEach(year => {
      map.set(year, true);
    });
    results.length = 0; // clear the array
    map.forEach((_, year) => {
      results.push(year);
    });
    // sort the results
    results.sort((a, b) => a - b);
    filters.years = results;
    if (results.length === 1) {
      filters.year = results[0];
    }
  }
  if (filters.year && singleYear && navtree) {
    if (
      filters.year &&
      !getYears(navtree, {
        ...filters,
        model: null
      }).includes(filters.year)
    ) {
      scratchText += " " + (singleYear as number).toString();
      filters.year = null;
      filters.years = null;
    }
  }
  filters.scratchText = scratchText;
  return text.trim();
}

function getConditionFromText(text: string, filters: Filters): string {
  let txt = text;
  let condIdx = text.indexOf("new");
  if (condIdx !== -1) {
    if (
      (condIdx === 0 || text[condIdx - 1] === " ") &&
      (condIdx + 3 === text.length ||
        text[condIdx + 3] === " " ||
        text[condIdx + 3] === "-" ||
        text[condIdx + 3] === ",")
    ) {
      filters.condition = "NEW";
      return (text.substring(0, condIdx) + text.substring(condIdx + 3)).trim();
    }
  }
  ["cpo", "used", "old", "certified", "pre-owned", "owned", "preowned"].some(
    c => {
      condIdx = text.indexOf(c);
      if (condIdx !== -1) {
        if (
          (condIdx === 0 || text[condIdx - 1] === " ") &&
          (condIdx + c.length === text.length ||
            text[condIdx + c.length] === " " ||
            text[condIdx + c.length] === "-" ||
            text[condIdx + c.length] === ",")
        ) {
          filters.condition = "CPO";
          txt = (
            text.substring(0, condIdx) + text.substring(condIdx + c.length)
          ).trim();
          return true;
        }
      }
      return false;
    }
  );
  return txt;
}

export function parseFreeText(
  text: string,
  accountMakes: string[],
  defFilters: Filters = defaultFilters,
  navtree: RootNode | null = null
): Filters {
  const filters: Filters = {
    ...defFilters,
    years: null,
    freeWords: null,
    scratchText: ""
  };
  if (!text || !accountMakes || accountMakes.length === 0) {
    return filters;
  }

  let txt = text.toLowerCase().trim().replace(/\s+/g, " ") || "";

  txt = getConditionFromText(txt, filters);
  txt = getMakeFromText(txt, accountMakes, filters);
  txt = getYearsFromText(txt, filters, navtree);

  txt = cleanString(txt);

  if (navtree && filters.make && filters.year && txt.length > 0) {
    const models = getModels(navtree, filters);
    if (models.length > 0) {
      const modelIdx = models.map(m => m.toLowerCase()).indexOf(txt);
      if (modelIdx !== -1) {
        filters.model = models[modelIdx];
        txt = "";
      }
    }
  }
  filters.freeWords = txt
    .split(" ")
    .filter(w => w && w.trim().length > 0 && w.match(/[a-z0-9]/gi));
  return filters;
}
